<script setup lang="ts">
import { computed, ref } from "vue";
import { useTranslation } from "i18next-vue";
import AnalysisGroupEntityItem from "./AnalysisGroupEntityItem.vue";
import type {
  MarkerPhotoState,
  PhotoAttachment,
  PlanListEntity,
} from "@/utils/plan-analysis/types";

/**
 * Liste des entités d'une carte ouverte — groupe d'analyse courant ou
 * observation. TOUJOURS visible quand la carte est ouverte (retirer un intrus
 * est la correction n°1). Chaque ligne sélectionne l'entité sur le plan ; une
 * photo au rattachement incertain porte un « ? » avec ses alternatives
 * (groupes d'analyse seulement) ; au-delà de 8, « Afficher tout ».
 */

const props = defineProps<{
  entities: PlanListEntity[];
  /** Rattachements photo du groupe (règle, alternatives), par marqueur. */
  photoAttachments?: PhotoAttachment[];
  /** Observation : état photo de chaque marqueur, par handle d'entité. */
  photoStates?: Map<string, MarkerPhotoState>;
  /** Infobulle du bouton « Retirer » (par défaut : retirer du groupe). */
  removeTitle?: string;
}>();

defineEmits<{
  (e: "select", handle: string): void;
  (e: "remove", handle: string): void;
  (e: "move", payload: { handle: string; targetGroupId: string }): void;
}>();

const { t } = useTranslation();

const attachmentOf = computed(
  () => new Map((props.photoAttachments ?? []).map((p) => [p.handle, p])),
);

/** Au-delà de ce nombre, la liste se tronque derrière « Afficher tout ». */
const VISIBLE_LIMIT = 8;
const showAll = ref(false);

const visibleEntities = computed(() =>
  showAll.value ? props.entities : props.entities.slice(0, VISIBLE_LIMIT),
);
const hiddenCount = computed(() =>
  Math.max(0, props.entities.length - VISIBLE_LIMIT),
);
</script>

<template>
  <div class="entity-list" @click.stop>
    <div class="entity-list-label">
      {{ t("canvas:analysis.entityList.title", { count: entities.length }) }}
    </div>
    <div class="d-flex flex-column">
      <AnalysisGroupEntityItem
        v-for="entity in visibleEntities"
        :key="entity.handle"
        :entity="entity"
        :attachment="attachmentOf.get(entity.handle)"
        :photo-state="photoStates?.get(entity.handle)"
        :remove-title="removeTitle"
        @select="$emit('select', entity.handle)"
        @remove="$emit('remove', entity.handle)"
        @move="$emit('move', { handle: entity.handle, targetGroupId: $event })"
      />
      <button
        v-if="hiddenCount > 0 && !showAll"
        type="button"
        class="entity-toggle"
        @click="showAll = true"
      >
        {{
          t("canvas:analysis.entityList.showAll", {
            count: entities.length,
          })
        }}
      </button>
      <button
        v-else-if="showAll && hiddenCount > 0"
        type="button"
        class="entity-toggle"
        @click="showAll = false"
      >
        {{ t("canvas:analysis.entityList.showLess") }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Même style que les labels Nom / Commentaire de la carte.
.entity-list-label {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  opacity: 0.7;
  margin-bottom: 3px;
}

.entity-toggle {
  border: none;
  background: transparent;
  color: var(--card-accent-light, var(--dr-report-light));
  font-size: 0.74rem;
  padding: 4px 0 2px;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
}
</style>
